// Proposal generator — builds a branded Esencee/SnC .docx proposal from the
// current simulation state and triggers a download. Each section is its own
// function so the template can be tweaked without touching the others.

import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  AlignmentType, BorderStyle, WidthType, HeadingLevel, PageBreak,
  ShadingType, ImageRun, convertInchesToTwip,
} from "docx";
import { saveAs } from "file-saver";

// Fetch the Esencee logo from /public as raw bytes for embedding in the .docx.
// Returns null if the fetch fails so the doc still renders without the image.
async function loadLogoBytes() {
  try {
    const res = await fetch("/esencee_logo.png");
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

// ─── Brand colour tokens (hex without leading #) ──────────────────────────────
const NAVY   = "1F3A5F";
const RED    = "C0392B";
const MID    = "2E75B6";
const LIGHT  = "BFD3F0";
const BG     = "EDF1F7";
const TEXT   = "1F3A5F";
const MUTED  = "6B7280";
const WHITE  = "FFFFFF";

const FONT = "Inter";
const CONTENT_WIDTH = 9746; // DXA — A4 with 1080 twip margins

// ─── Tiny helpers for paragraphs / runs ───────────────────────────────────────
function run(text, opts = {}) {
  return new TextRun({
    text: String(text ?? ""),
    font: FONT,
    size: opts.size ?? 20, // half-points (20 = 10pt)
    bold: opts.bold ?? false,
    italics: opts.italics ?? false,
    color: opts.color ?? TEXT,
  });
}
function p(text, opts = {}) {
  return new Paragraph({
    alignment: opts.align ?? AlignmentType.LEFT,
    spacing: { before: opts.before ?? 0, after: opts.after ?? 80 },
    children: Array.isArray(text)
      ? text
      : [run(text, opts)],
  });
}
function blank() { return p(""); }
function pageBreak() {
  return new Paragraph({ children: [new PageBreak()] });
}

// Styled section heading: navy text, red underline (border-bottom).
function sectionHeading(num, title) {
  return new Paragraph({
    spacing: { before: 280, after: 120 },
    border: { bottom: { color: RED, size: 12, space: 4, style: BorderStyle.SINGLE } },
    children: [
      run(`${num}  `, { size: 26, bold: true, color: NAVY }),
      run(title, { size: 26, bold: true, color: NAVY }),
    ],
  });
}
function subHeading(title) {
  return new Paragraph({
    spacing: { before: 180, after: 80 },
    children: [run(title, { size: 22, bold: true, color: NAVY })],
  });
}

// Format helpers
function fmtKes(n) {
  if (n == null || isNaN(n)) return "—";
  return "KES " + Math.round(n).toLocaleString("en-GB");
}
function fmtNum(n, dp = 0) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function todayLong() {
  return new Date().toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
}

// ─── Reusable styled table builders ───────────────────────────────────────────
function brandCell(text, opts = {}) {
  const isHeader = opts.header;
  return new TableCell({
    width: opts.width ? { size: opts.width, type: WidthType.DXA } : undefined,
    shading: isHeader
      ? { type: ShadingType.CLEAR, fill: NAVY }
      : opts.alt
      ? { type: ShadingType.CLEAR, fill: BG }
      : undefined,
    margins: { top: 80, bottom: 80, left: 100, right: 100 },
    children: [new Paragraph({
      alignment: opts.align ?? AlignmentType.LEFT,
      spacing: { before: 0, after: 0 },
      children: [run(text, {
        size: opts.size ?? 20,
        bold: isHeader || opts.bold || false,
        color: isHeader ? WHITE : opts.color || TEXT,
      })],
    })],
  });
}

// rows: array of { cells: [string, string, ...], header?: bool }
function buildTable(rows, columnWidths) {
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths,
    borders: {
      top:    { style: BorderStyle.SINGLE, size: 4, color: NAVY },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: NAVY },
      left:   { style: BorderStyle.SINGLE, size: 4, color: NAVY },
      right:  { style: BorderStyle.SINGLE, size: 4, color: NAVY },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "D6DEE8" },
      insideVertical:   { style: BorderStyle.SINGLE, size: 2, color: "D6DEE8" },
    },
    rows: rows.map((r, i) => new TableRow({
      tableHeader: r.header,
      children: r.cells.map((c, j) =>
        brandCell(c, {
          header: r.header,
          alt: !r.header && i % 2 === 1,
          width: columnWidths?.[j],
          align: r.aligns?.[j],
          bold: r.bold,
        })
      ),
    })),
  });
}

// Single-cell full-width navy band — used as cover-page background.
function navyBand(children, heightDxa) {
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    rows: [new TableRow({
      height: heightDxa ? { value: heightDxa, rule: "exact" } : undefined,
      children: [new TableCell({
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        shading: { type: ShadingType.CLEAR, fill: NAVY },
        margins: { top: 400, bottom: 400, left: 300, right: 300 },
        children,
      })],
    })],
    borders: noBorders(),
  });
}
function noBorders() {
  return {
    top:    { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    left:   { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right:  { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    insideVertical:   { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
  };
}

// ─── Section builders ─────────────────────────────────────────────────────────

function buildCover(d, logoBytes) {
  // Logo paragraph: image if we have bytes, else fall back to text wordmark.
  const logoPara = logoBytes
    ? new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { before: 0, after: 200 },
        children: [new ImageRun({
          data: logoBytes,
          transformation: { width: 220, height: 60 },
        })],
      })
    : new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { before: 0, after: 200 },
        children: [
          run("Sn", { size: 56, bold: true, color: WHITE }),
          run("C",  { size: 56, bold: true, color: RED  }),
        ],
      });

  return [
    blank(), blank(),
    navyBand([
      logoPara,
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 400 },
        children: [run("ESENCEE ENERGY LIMITED", { size: 22, bold: true, color: WHITE })],
      }),
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 200 },
        children: [run("PROPOSAL", { size: 36, bold: true, color: LIGHT })],
      }),
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 100 },
        children: [run(d.projectTitle, { size: 32, bold: true, color: WHITE })],
      }),
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 600 },
        children: [run(`Prepared for ${d.clientName || "Client"}`, { size: 22, color: LIGHT })],
      }),
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 60 },
        children: [run(`Reference   ${d.refNo}`, { size: 18, color: LIGHT })],
      }),
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 60 },
        children: [run(`Date            ${todayLong()}`, { size: 18, color: LIGHT })],
      }),
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 60 },
        children: [run(`Validity        ${d.validity || 30} days`, { size: 18, color: LIGHT })],
      }),
    ], 11000),
    pageBreak(),
  ];
}

function buildCoverLetter(d) {
  return [
    p(todayLong(), { size: 20 }),
    blank(),
    p(d.clientName || "Client", { bold: true, size: 22 }),
    p(d.siteLocation || "", { size: 20, color: MUTED }),
    blank(),
    p("Dear Sir / Madam,", { size: 22, bold: true }),
    blank(),
    p([
      run("Subject: ", { size: 22, bold: true }),
      run(`${d.projectTitle} — Solar PV ${d.bessKwh > 0 ? "+ Battery Storage" : ""} Proposal (Ref ${d.refNo})`, { size: 22 }),
    ]),
    blank(),
    p(
      `Esencee Energy Limited (SnC) is pleased to submit this proposal for the design, supply, and installation of a ${d.pvKwp} kWp grid-tied solar photovoltaic system${d.bessKwh > 0 ? ` integrated with a ${d.bessKwh} kWh lithium battery energy storage system (BESS)` : ""} at ${d.siteLocation || "your premises"}.`,
      { size: 22 }
    ),
    blank(),
    p(
      "This proposal is the output of our SolarSim Pro engineering platform, applied to your measured load profile and your KPLC tariff structure. The sizing, dispatch, and financial model presented here reflect site-specific irradiance from PVGIS, your demand basis, and validated commercial assumptions.",
      { size: 22 }
    ),
    blank(),
    p(
      `Based on the analysis, the proposed system will displace ${d.selfSufficiencyPct || "—"}% of your current grid + diesel consumption, deliver Year-1 savings of ${fmtKes(d.annualSavingsY1)}, and pay back in approximately ${d.paybackYears || "—"} years against a 25-year asset life.`,
      { size: 22 }
    ),
    blank(),
    subHeading("System at a glance"),
    buildTable([
      { header: true,  cells: ["Metric", "Value", "Notes"] },
      { cells: ["Solar PV capacity",         `${d.pvKwp} kWp`,           `${d.numModules} × ${d.moduleWp || 580} Wp Tier-1 panels`] },
      { cells: ["Battery storage (BESS)",    d.bessKwh ? `${d.bessKwh} kWh` : "—", d.bessKwh ? `${d.bessUnits || "—"} × ${d.bessUnitKwh || 100} kWh LiFePO₄ modules` : "Grid-tied only"] },
      { cells: ["Self-sufficiency",          `${d.selfSufficiencyPct || "—"} %`, "Consumption met by solar + BESS"] },
      { cells: ["Year-1 energy savings",     fmtKes(d.annualSavingsY1), "KPLC + diesel offset"] },
      { cells: ["Indicative IRR",            d.irrPct ? `${d.irrPct} %` : "—", "Pre-tax, asset-life basis"] },
      { cells: ["CO₂ avoided",               d.co2Annual ? `${d.co2Annual} t/yr` : "—", "Lifecycle carbon offset"] },
    ], [3000, 2800, 3946]),
    blank(),
    p("We thank you for the opportunity and remain available to walk you through the assumptions, scope, and commercial terms in detail.", { size: 22 }),
    blank(),
    p("Yours faithfully,", { size: 22 }),
    blank(), blank(),
    p("_____________________________", { size: 22 }),
    p(d.preparedBy || "Romeet Patel", { size: 22, bold: true }),
    p("Esencee Energy Limited", { size: 20, color: MUTED }),
    p("engineering@esencee.com", { size: 20, color: MID }),
    pageBreak(),
  ];
}

function buildTOC() {
  const items = [
    "1.   Current Situation",
    "2.   Proposed Solution",
    "3.   System Specifications",
    "4.   Energy & Financial Analysis",
    "5.   Key Benefits",
    "6.   Scope of Works",
    "7.   Implementation Timeline",
    "8.   Commercial Summary",
    "9.   Model Assumptions & Demand Basis",
    "10. Authorisation",
  ];
  return [
    sectionHeading("", "Table of Contents"),
    ...items.map(i => p(i, { size: 22 })),
    pageBreak(),
  ];
}

function buildSection1_CurrentSituation(d) {
  return [
    sectionHeading("1.", "Current Situation"),
    p(
      `${d.clientName || "The client"} operates from ${d.siteLocation || "the site"}, drawing power from the KPLC grid${d.gridPct < 1 ? ` supplemented by on-site diesel generation (${Math.round((1 - d.gridPct) * 100)}%)` : ""}. Measured load profile shows an average demand of ${fmtNum(d.dailyKwh, 0)} kWh per day with a peak of ${fmtNum(d.peakKw, 1)} kW.`,
      { size: 22 }
    ),
    blank(),
    subHeading("Demand basis"),
    buildTable([
      { header: true, cells: ["Parameter", "Value", "Source"] },
      { cells: ["Daily energy demand",     `${fmtNum(d.dailyKwh, 0)} kWh`, d.demandSource || "Power logger / utility bills"] },
      { cells: ["Peak demand",             `${fmtNum(d.peakKw, 1)} kW`,    "Measured / declared"] },
      { cells: ["Annual energy",           `${fmtNum(d.annualKwh, 0)} kWh`,"Effective operating calendar"] },
      { cells: ["Blended tariff (Y1)",     `KES ${fmtNum(d.blendedRateY1, 2)} / kWh`, `${Math.round((d.gridPct || 0) * 100)}% grid + ${Math.round((1 - (d.gridPct || 0)) * 100)}% diesel`] },
    ], [3500, 2500, 3746]),
  ];
}

function buildSection2_ProposedSolution(d) {
  return [
    sectionHeading("2.", "Proposed Solution"),
    p(
      `We propose the design, supply, installation, and commissioning of a ${d.pvKwp} kWp grid-tied solar PV system${d.bessKwh > 0 ? `, paired with a ${d.bessKwh} kWh lithium-iron-phosphate (LiFePO₄) Battery Energy Storage System` : ""}, integrated with the existing electrical infrastructure at ${d.siteLocation || "site"}.`,
      { size: 22 }
    ),
    blank(),
    p(
      `The PV array uses ${d.numModules} × ${d.moduleWp || 580} Wp Tier-1 mono-PERC modules${d.bessKwh > 0 ? `, dispatched through a ${Math.ceil(d.pvKwp || 0)} kW hybrid inverter with battery management` : ` through a ${Math.ceil(d.pvKwp || 0)} kW grid-tied inverter`}. The system is designed to maximise solar self-consumption during the working day and${d.bessKwh > 0 ? ` to time-shift surplus generation into evening / off-sun periods via the BESS` : " to reduce peak demand charges during peak grid hours"}.`,
      { size: 22 }
    ),
  ];
}

function buildSection3_Specifications(d) {
  return [
    sectionHeading("3.", "System Specifications"),
    buildTable([
      { header: true, cells: ["Component", "Specification", "Quantity"] },
      { cells: ["Solar PV modules",    `${d.moduleWp || 580} Wp Tier-1 Mono-PERC`,             `${d.numModules}`] },
      { cells: ["DC system size",      `${d.pvKwp} kWp`,                                       "—"] },
      { cells: ["Inverter",            `${Math.ceil(d.pvKwp || 0)} kW ${d.bessKwh > 0 ? "Hybrid" : "Grid-tied"}`, "1"] },
      { cells: ["BESS modules",        d.bessKwh ? `LiFePO₄ ${d.bessUnitKwh || 100} kWh each` : "—", d.bessUnits ? `${d.bessUnits}` : "—"] },
      { cells: ["BESS total capacity", d.bessKwh ? `${d.bessKwh} kWh usable` : "—",            "—"] },
      { cells: ["Mounting structure",  "Anodised aluminium with HDG steel subframe",           "1 lot"] },
      { cells: ["DC/AC cabling",       "TÜV-rated 6 mm² DC + AC mains/sub-circuits",           "1 lot"] },
      { cells: ["Protection",          "DC/AC SPDs, MCBs, RCDs, earthing & lightning",         "1 set"] },
      { cells: ["Monitoring",          "SCADA, 5-year remote monitoring subscription",         "1"] },
      { cells: ["Design life",         "25 years (modules), 10 years (BESS, with replacement)", "—"] },
    ], [3500, 4500, 1746]),
  ];
}

function buildSection4_Financial(d) {
  return [
    sectionHeading("4.", "Energy & Financial Analysis"),
    subHeading("Tariff baseline"),
    p(`Year-1 blended tariff: KES ${fmtNum(d.blendedRateY1, 2)} / kWh, derived from your KPLC component formula (HR + LR + FEC + FERFA + Inflation Adj + ERC + WRA) × 1.05${d.gridPct < 1 ? ` blended with diesel at ${Math.round((1 - d.gridPct) * 100)}% of supply` : ""}.`, { size: 22 }),
    blank(),
    subHeading("Annual savings — Year 1"),
    buildTable([
      { header: true, cells: ["Stream", "Value"] },
      { cells: ["Year-1 energy savings",       fmtKes(d.annualSavingsY1)] },
      { cells: ["CO₂ avoided",                 d.co2Annual ? `${d.co2Annual} t / year` : "—"] },
      { cells: ["Equivalent grid + diesel offset", `${fmtNum(d.annualKwh, 0)} kWh / year`] },
    ], [4500, 5246]),
    blank(),
    subHeading("Investment summary"),
    buildTable([
      { header: true, cells: ["Item", "Value"] },
      { cells: ["Total system cost (excl. VAT)", fmtKes(d.totalExclVat)] },
      { cells: ["VAT (6% — KE Solar exempt scheme)", fmtKes(d.vat)] },
      { cells: ["Total project cost (incl. VAT)", fmtKes(d.totalInclVat)] },
      { cells: ["Simple payback",                  d.paybackYears ? `${d.paybackYears} years` : "—"] },
      { cells: ["Indicative IRR",                  d.irrPct ? `${d.irrPct} %` : "—"] },
      { cells: ["25-year NPV (client view)",       fmtKes(d.npv25Client)] },
    ], [4500, 5246]),
  ];
}

function buildSection5_Benefits() {
  const benefits = [
    ["Energy cost reduction",  "Direct displacement of KPLC and diesel consumption."],
    ["Tariff hedging",         "Protection against KPLC tariff escalation and diesel price volatility."],
    ["Operational resilience", "Reduced exposure to grid outages via on-site generation and storage."],
    ["ESG positioning",        "Quantified CO₂ reduction supporting client sustainability commitments."],
    ["Asset value",            "Improved property/operations P&L with a 25-year-life capital asset."],
    ["Scalability",            "Modular design — capacity additions possible without redesign."],
  ];
  return [
    sectionHeading("5.", "Key Benefits"),
    buildTable([
      { header: true, cells: ["Benefit", "Detail"] },
      ...benefits.map(b => ({ cells: b })),
    ], [3000, 6746]),
  ];
}

function buildSection6_Scope() {
  const items = [
    "Detailed engineering design, single-line diagrams, structural and electrical drawings.",
    "Procurement and supply of all PV, BESS, BOS, mounting, cabling and protection equipment.",
    "Civil works for mounting, cable trenching and combiner/distribution housings.",
    "Mechanical installation: mounting structure, modules, BESS racks/enclosures.",
    "Electrical installation: DC/AC cabling, inverter, distribution boards, protections, earthing.",
    "Commissioning, testing, performance verification and KPLC liaison (where applicable).",
    "Operator training, handover documentation, O&M plan and warranty package.",
  ];
  return [
    sectionHeading("6.", "Scope of Works"),
    ...items.map(t => p(`•  ${t}`, { size: 22 })),
  ];
}

function buildSection7_Timeline() {
  return [
    sectionHeading("7.", "Implementation Timeline"),
    buildTable([
      { header: true, cells: ["Phase", "Duration", "Milestone"] },
      { cells: ["Engineering & approvals",   "2 – 3 weeks", "Drawings, BOQ, regulatory clearances"] },
      { cells: ["Procurement & logistics",   "4 – 8 weeks", "Equipment delivered to site"] },
      { cells: ["Civil & mechanical works",  "2 – 4 weeks", "Mounting structure complete"] },
      { cells: ["Electrical installation",   "2 – 3 weeks", "Modules wired, inverter integrated"] },
      { cells: ["Testing & commissioning",   "1 – 2 weeks", "Performance test, handover"] },
      { cells: ["Total indicative",          "11 – 20 weeks", "From contract signature to commissioning"] },
    ], [3500, 2500, 3746]),
  ];
}

function buildSection8_Commercial(d) {
  return [
    sectionHeading("8.", "Commercial Summary"),
    p("The pricing below is indicative and reflects the simulation outputs; line-item pricing can be re-issued in full BOQ format on request.", { size: 22, italics: true, color: MUTED }),
    blank(),
    buildTable([
      { header: true, cells: ["Description", "Amount (KES)"] },
      { cells: ["1. Solar PV array — modules, inverter, monitoring", ""] },
      { cells: ["2. Battery Energy Storage System",                  ""] },
      { cells: ["3. Aluminium mounting framing",                     ""] },
      { cells: ["4. DC / AC cabling",                                ""] },
      { cells: ["5. Electrical protections, SPD & earthing",         ""] },
      { cells: ["6. Labour, installation, transport & technical services", ""] },
      { cells: ["7. Project management, design & commissioning",      ""] },
      { cells: ["Subtotal (excl. VAT)",                              fmtKes(d.totalExclVat)] },
      { cells: ["VAT (6%)",                                          fmtKes(d.vat)] },
      { cells: ["Total (incl. VAT)",                                 fmtKes(d.totalInclVat)] },
    ], [6500, 3246]),
    blank(),
    subHeading("Payment milestones"),
    buildTable([
      { header: true, cells: ["Milestone", "%"] },
      { cells: ["On contract signature",                       `${d.deposit1 || 40}%`] },
      { cells: ["On delivery of major equipment to site",      `${d.deposit2 || 40}%`] },
      { cells: ["On commissioning & handover",                 `${100 - (d.deposit1 || 40) - (d.deposit2 || 40)}%`] },
    ], [6500, 3246]),
    blank(),
    p(`Validity: ${d.validity || 30} days from the proposal date.`, { size: 20, italics: true, color: MUTED }),
  ];
}

function buildSection9_Assumptions(d) {
  const lines = [
    `Demand basis: ${d.demandSource || "Measured power logger profile / utility bill aggregation"}.`,
    `Effective operating calendar: 261 weekdays + ${d.satFactor || "n"}×Saturdays + (52 + public holidays)×Sundays.`,
    "Solar generation modelled across all 365 days (the sun does not take weekends).",
    `Irradiance data: ${d.irrSource || "PVGIS live"} for site coordinates ${d.lat || "—"}, ${d.lng || "—"}.`,
    "VAT applied at 6% on total project cost, in line with Kenya VAT Act exemptions for solar PV components.",
    "KPLC tariff formula: (HR + LR + FEC + FERFA + Inflation Adj + ERC + WRA) × 1.05, averaged across supplied bills.",
    `Diesel blend: ${Math.round((1 - (d.gridPct || 1)) * 100)}% of pre-solar consumption, modelled at KES ${d.dieselPrice || 120}/L × ${d.dieselSfc || 0.35} L/kWh × ${d.gensetOmFactor || 1.20} O&M factor.`,
    `Project life: ${d.projectLifeYears || 25} years, with a tariff escalation of ${d.escalation || "—"}%.`,
    "BESS round-trip efficiency: 92%, depth-of-discharge 90%, calendar life 10 years (replacement budgeted at year 10).",
  ];
  return [
    sectionHeading("9.", "Model Assumptions & Demand Basis"),
    ...lines.map(t => p(`•  ${t}`, { size: 21 })),
  ];
}

function buildSection10_Authorisation(d) {
  return [
    sectionHeading("10.", "Authorisation"),
    p("Acceptance of this proposal authorises Esencee Energy Limited to commence detailed engineering, procurement and the contracting process. Both parties confirm the scope and commercial terms by signing below.", { size: 22 }),
    blank(), blank(),
    buildTable([
      { header: true, cells: ["For the Client", "For Esencee Energy"] },
      { cells: ["Name:  ____________________________", "Name:  Romeet Patel"] },
      { cells: ["Title:  ____________________________", "Title:  Director / Engineering"] },
      { cells: ["Signed: ____________________________", "Signed: ____________________________"] },
      { cells: ["Date:    ____________________________", `Date:    ${todayLong()}`] },
    ], [4873, 4873]),
    blank(), blank(),
    p("Esencee Energy Limited  ·  engineering@esencee.com  ·  Nairobi, Kenya", {
      size: 18, color: MUTED, align: AlignmentType.CENTER,
    }),
    p("Engineering is truth. Mathematics is the language.", {
      size: 18, color: MUTED, italics: true, align: AlignmentType.CENTER,
    }),
  ];
}

// ─── Top-level builder ────────────────────────────────────────────────────────
function shapeData({ site, assumptions, results, irrMeta, refNo, validity, preparedBy, totalExclVat, vat, totalInclVat, paybackYears, npv25Client, deposit1, deposit2 }) {
  return {
    clientName: site?.clientName || "",
    siteLocation: site?.location || "",
    lat: site?.lat, lng: site?.lng,
    refNo: refNo || `QT-${new Date().getFullYear()}-0000`,
    validity, preparedBy,
    pvKwp: results?.actualPvKwp ?? 0,
    bessKwh: results?.bessActual ?? 0,
    numModules: results?.numModules ?? 0,
    bessUnits: results?.bessUnits ?? 0,
    moduleWp: assumptions?.moduleWp,
    bessUnitKwh: assumptions?.bessUnitKwh,
    selfSufficiencyPct: results?.selfSufficiencyPct,
    annualSavingsY1: results?.annualSavingsY1,
    blendedRateY1: results?.blendedRateY1,
    gridPct: results?.gridPct ?? 1,
    co2Annual: results?.co2Annual,
    dailyKwh: results?.dailyKwh,
    peakKw: results?.peakKw,
    annualKwh: results?.dailyKwh ? Math.round(results.dailyKwh * 365) : null,
    irrPct: results?.irrPct,
    paybackYears,
    totalExclVat, vat, totalInclVat,
    npv25Client,
    projectLifeYears: assumptions?.projectLifeYears,
    escalation: assumptions?.tariffEscalation,
    dieselPrice: assumptions?.dieselPricePerL,
    dieselSfc: assumptions?.dieselSfc,
    gensetOmFactor: assumptions?.gensetOmFactor,
    irrSource: irrMeta?.source || (irrMeta?.fromTable ? "Table fallback" : "PVGIS live"),
    deposit1, deposit2,
    projectTitle: results?.actualPvKwp
      ? `${results.actualPvKwp} kWp Solar PV${results.bessActual ? ` + ${results.bessActual} kWh BESS` : ""}`
      : "Solar Energy Solution",
  };
}

export async function generateProposal(input) {
  const d = shapeData(input);
  const logoBytes = await loadLogoBytes();

  const doc = new Document({
    creator: "Esencee Energy",
    title: `${d.clientName} Proposal ${d.refNo}`,
    styles: {
      default: {
        document: {
          run: { font: FONT, size: 20, color: TEXT },
          paragraph: { spacing: { line: 280 } },
        },
      },
    },
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(0.75),
            bottom: convertInchesToTwip(0.75),
            left: convertInchesToTwip(0.75),
            right: convertInchesToTwip(0.75),
          },
        },
      },
      children: [
        ...buildCover(d, logoBytes),
        ...buildCoverLetter(d),
        ...buildTOC(),
        ...buildSection1_CurrentSituation(d),
        blank(),
        ...buildSection2_ProposedSolution(d),
        blank(),
        ...buildSection3_Specifications(d),
        pageBreak(),
        ...buildSection4_Financial(d),
        pageBreak(),
        ...buildSection5_Benefits(),
        blank(),
        ...buildSection6_Scope(),
        pageBreak(),
        ...buildSection7_Timeline(),
        blank(),
        ...buildSection8_Commercial(d),
        pageBreak(),
        ...buildSection9_Assumptions(d),
        pageBreak(),
        ...buildSection10_Authorisation(d),
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  const safe = (s) => (s || "Proposal").replace(/[\\/:*?"<>|]/g, "").trim() || "Proposal";
  const fileName = `${safe(d.refNo)} ${safe(d.clientName)} Proposal.docx`;
  saveAs(blob, fileName);
}
